import { useEffect, useState, type KeyboardEvent } from 'react';
import { X as XIcon } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogClose,
} from '@/components/ui/Dialog';
import { FileUploader } from '@/components/forms/FileUploader';
import type { User } from '@/lib/api/types';
import { useT } from '@/lib/i18n/useT';

/**
 * EditProfileDialog — updates the user's editable profile fields via PATCH /profile.
 *
 * Display-only fields (email, role, companyId) are never sent. Avatar and cover
 * are uploaded via the shared FileUploader (which POSTs to /upload).
 */

export interface EditProfileInput {
  firstname?: string;
  lastname?: string;
  username?: string | null;
  phone?: string | null;
  title?: string | null;
  location?: string | null;
  studiedAt?: string | null;
  bio?: string | null;
  picture?: string | null;
  coverPicture?: string | null;
  languages?: string[];
  interests?: string[];
}

interface FormState {
  firstname: string;
  lastname: string;
  username: string;
  phone: string;
  title: string;
  location: string;
  studiedAt: string;
  bio: string;
  picture: string;
  coverPicture: string;
  languages: string[];
  interests: string[];
}

function seed(profile: User | null): FormState {
  return {
    firstname: profile?.firstname ?? '',
    lastname: profile?.lastname ?? '',
    username: profile?.username ?? '',
    phone: profile?.phone ?? '',
    title: profile?.title ?? '',
    location: profile?.location ?? '',
    studiedAt: profile?.studiedAt ?? '',
    bio: profile?.bio ?? '',
    picture: profile?.picture ?? '',
    coverPicture: profile?.coverPicture ?? '',
    languages: profile?.languages ?? [],
    interests: profile?.interests ?? [],
  };
}

export function EditProfileDialog({
  open,
  onOpenChange,
  profile,
  onSave,
  loading,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  profile: User | null;
  onSave: (v: EditProfileInput) => void;
  loading?: boolean;
}) {
  const t = useT();
  const [form, setForm] = useState<FormState>(() => seed(profile));
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(seed(profile));
      setUploading(false);
    }
  }, [open, profile]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const canSave = form.firstname.trim().length >= 1 && !loading && !uploading;
  const submit = () => {
    if (!canSave) return;
    onSave({
      firstname: form.firstname.trim(),
      lastname: form.lastname.trim(),
      username: form.username.trim() || null,
      phone: form.phone.trim() || null,
      title: form.title.trim() || null,
      location: form.location.trim() || null,
      studiedAt: form.studiedAt.trim() || null,
      bio: form.bio.trim() || null,
      picture: form.picture || null,
      coverPicture: form.coverPicture || null,
      languages: form.languages,
      interests: form.interests,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="scrollbar-thin max-h-[90vh] max-w-[640px] overflow-y-auto p-0">
        <div className="p-8">
          <header className="mb-6">
            <div
              className="mb-2 text-[11px] font-extrabold uppercase tracking-[0.10em] text-[var(--color-xk-accent)]"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              {t('editProfile.eyebrow')}
            </div>
            <h2
              className="mb-1.5 text-[22px] font-black tracking-tight text-[var(--color-xk-lavender)]"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              {t('editProfile.title')}
            </h2>
            <p className="text-[13px] text-[var(--color-xk-text-muted)]">
              {t('editProfile.subtitle')}
            </p>
          </header>

          {/* Visuals */}
          <Section label={t('editProfile.section.visuals')}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <FieldLabel>{t('editProfile.field.avatar')}</FieldLabel>
                <FileUploader
                  value={form.picture || null}
                  onChange={(v) => set('picture', v ?? '')}
                  onUploadingChange={setUploading}
                  preset="avatar"
                />
              </div>
              <div>
                <FieldLabel>{t('editProfile.field.cover')}</FieldLabel>
                <FileUploader
                  value={form.coverPicture || null}
                  onChange={(v) => set('coverPicture', v ?? '')}
                  onUploadingChange={setUploading}
                  preset="photo"
                  editorAspect={16 / 9}
                />
              </div>
            </div>
          </Section>

          {/* Identity */}
          <Section label={t('editProfile.section.identity')}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <FieldLabel required>{t('editProfile.field.firstName')}</FieldLabel>
                <input
                  type="text"
                  value={form.firstname}
                  onChange={(e) => set('firstname', e.target.value)}
                  autoFocus
                  className={inputCls}
                />
              </div>
              <div>
                <FieldLabel>{t('editProfile.field.lastName')}</FieldLabel>
                <input
                  type="text"
                  value={form.lastname}
                  onChange={(e) => set('lastname', e.target.value)}
                  className={inputCls}
                />
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-4">
              <div>
                <FieldLabel>{t('editProfile.field.username')}</FieldLabel>
                <input
                  type="text"
                  value={form.username}
                  onChange={(e) => set('username', e.target.value)}
                  placeholder="marco"
                  className={inputCls}
                />
              </div>
              <div>
                <FieldLabel>{t('editProfile.field.phone')}</FieldLabel>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => set('phone', e.target.value)}
                  placeholder="+1 555…"
                  className={inputCls}
                />
              </div>
            </div>
            <div className="mt-3">
              <FieldLabel>{t('editProfile.field.bio')}</FieldLabel>
              <textarea
                rows={3}
                value={form.bio}
                onChange={(e) => set('bio', e.target.value)}
                placeholder={t('editProfile.field.bioPlaceholder')}
                className={`${inputCls} min-h-[84px] resize-y`}
              />
            </div>
          </Section>

          {/* Professional */}
          <Section label={t('editProfile.section.professional')}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <FieldLabel>{t('editProfile.field.title')}</FieldLabel>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => set('title', e.target.value)}
                  placeholder={t('editProfile.field.titlePlaceholder')}
                  className={inputCls}
                />
              </div>
              <div>
                <FieldLabel>{t('editProfile.field.location')}</FieldLabel>
                <input
                  type="text"
                  value={form.location}
                  onChange={(e) => set('location', e.target.value)}
                  placeholder="San Antonio, TX"
                  className={inputCls}
                />
              </div>
            </div>
            <div className="mt-3">
              <FieldLabel>{t('editProfile.field.studiedAt')}</FieldLabel>
              <input
                type="text"
                value={form.studiedAt}
                onChange={(e) => set('studiedAt', e.target.value)}
                placeholder="UT Austin"
                className={inputCls}
              />
            </div>
          </Section>

          {/* Tags */}
          <Section label={t('editProfile.section.tags')}>
            <FieldLabel>{t('profile.sidebar.languages')}</FieldLabel>
            <TagInput value={form.languages} onChange={(v) => set('languages', v)} />
            <div className="h-3" />
            <FieldLabel>{t('profile.sidebar.interests')}</FieldLabel>
            <TagInput value={form.interests} onChange={(v) => set('interests', v)} />
          </Section>

          {/* Footer */}
          <div className="mt-6 flex justify-end gap-3 border-t border-[var(--color-xk-border)] pt-5">
            <DialogClose asChild>
              <button
                type="button"
                className="rounded-lg border border-[var(--color-xk-border)] px-5 py-2.5 text-[13px] font-bold text-[var(--color-xk-lavender)] hover:bg-[var(--color-xk-surface)]"
              >
                {t('common.cancel')}
              </button>
            </DialogClose>
            <button
              type="button"
              onClick={submit}
              disabled={!canSave}
              className="rounded-lg bg-[var(--color-xk-accent)] px-5 py-2.5 text-[13px] font-extrabold text-[#0A0A0A] transition-colors hover:bg-[var(--color-xk-accent-hover)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? t('common.saving') : t('editProfile.save')}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Pieces ──────────────────────────────────────────────────────────────

const inputCls =
  'block w-full rounded-lg border border-[var(--color-xk-border)] bg-[rgb(255_255_255/0.05)] px-3 py-2.5 text-[13px] text-[var(--color-xk-lavender)] placeholder:text-[var(--color-xk-text-muted)] focus:border-[var(--color-xk-accent)] focus:outline-none';

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-5 last:mb-0">
      <div
        className="mb-3 text-[10px] font-extrabold uppercase tracking-[0.14em] text-[var(--color-xk-accent)]"
        style={{ fontFamily: 'var(--font-display)' }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function FieldLabel({
  children,
  required,
}: {
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <label
      className="mb-1.5 block text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]"
      style={{ fontFamily: 'var(--font-display)' }}
    >
      {children}
      {required && <span className="ml-1 text-[var(--color-xk-accent)]">*</span>}
    </label>
  );
}

function TagInput({
  value,
  onChange,
}: {
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const [draft, setDraft] = useState('');
  const commit = () => {
    const v = draft.trim();
    if (!v) return;
    if (value.includes(v)) {
      setDraft('');
      return;
    }
    onChange([...value, v]);
    setDraft('');
  };
  const remove = (t: string) => onChange(value.filter((x) => x !== t));
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };
  const t = useT();
  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-[var(--color-xk-border)] bg-[rgb(255_255_255/0.05)] p-2">
      {value.map((tag) => (
        <span
          key={tag}
          className="inline-flex items-center gap-1 rounded-md bg-[var(--color-xk-accent)] px-2 py-1 text-[11px] font-extrabold text-[#0A0A0A]"
        >
          {tag}
          <button
            type="button"
            onClick={() => remove(tag)}
            aria-label={t('editProfile.tag.remove', { tag })}
            className="rounded-sm hover:bg-[rgb(0_0_0/0.15)]"
          >
            <XIcon className="h-3 w-3" />
          </button>
        </span>
      ))}
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKey}
        onBlur={commit}
        placeholder={value.length === 0 ? t('editProfile.tag.placeholder') : ''}
        className="min-w-[140px] flex-1 bg-transparent text-[13px] text-[var(--color-xk-lavender)] placeholder:text-[var(--color-xk-text-muted)] focus:outline-none"
      />
    </div>
  );
}
